// components/flow/components/config/VideoConfig.tsx
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Video as VideoIcon, Play, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useToast } from '@/hooks/use-toast.ts';
import { updateNode } from '@/services/api';
import type { FlowNode } from '@/types/flow.ts';
import MediaLibraryModal from '../MediaLibraryModal.tsx';

interface VideoConfigProps {
    node: FlowNode;
    onClose: () => void;
    pageId: string;
}

export default function VideoConfig({ node, onClose, pageId }: VideoConfigProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [nodeName, setNodeName] = useState(node.data.node_name || '');
    const [mediaUrl, setMediaUrl] = useState(node.data.config.media_url || '');
    const [mediaFilename, setMediaFilename] = useState(node.data.config.media_filename || '');
    const [attachmentId, setAttachmentId] = useState(node.data.config.attachment_id || '');
    const [typingSeconds, setTypingSeconds] = useState(node.data.config?.delay_seconds || 0);

    const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

    // Mutation para atualizar node
    const updateMutation = useMutation({
        mutationFn: (data: any) => updateNode(node.data.id, data),
        onSuccess: () => {
            toast({
                title: 'Configurações salvas!',
                description: 'O vídeo foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['funnel-details'] });
            onClose();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handler de salvar
    const handleSave = () => {
        if (!mediaUrl) {
            toast({
                variant: 'destructive',
                title: 'Vídeo obrigatório',
                description: 'Por favor, selecione um vídeo.',
            });
            return;
        }

        updateMutation.mutate({
            nome: nodeName,
            media_url: mediaUrl,
            media_filename: mediaFilename,
            media_type: 'video',
            attachment_id: attachmentId || null,
            delay_seconds: typingSeconds || null,
            page_id: pageId
        });
    };

    // Handler de seleção de mídia
    const handleMediaSelect = (media: any) => {
        console.log('📹 Mídia selecionada:', media);

        setMediaUrl(media.file_url);
        setMediaFilename(media.filename);

        if (media.attachment_id) {
            setAttachmentId(media.attachment_id);
            console.log('✅ Attachment ID capturado:', media.attachment_id);
        } else {
            console.warn('⚠️ Nenhum attachment_id encontrado na mídia');
            setAttachmentId('');
        }
    };

    return (
        <>
            <div className="flex-1 overflow-auto">
                <div className="p-6 space-y-6">
                    {/* Nome do Node */}
                    <div>
                        <Label htmlFor="node-name">Nome do Node (opcional)</Label>
                        <Input
                            id="node-name"
                            value={nodeName}
                            onChange={(e) => setNodeName(e.target.value)}
                            placeholder="Enviar Vídeo"
                            className="mt-1.5"
                            disabled={updateMutation.isPending}
                        />
                    </div>

                    {/* Vídeo */}
                    <div>
                        <Label>Vídeo *</Label>
                        {mediaUrl ? (
                            <div className="mt-1.5 space-y-2">
                                <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden border border-gray-200">
                                    <video
                                        src={mediaUrl}
                                        className="w-full h-full object-cover"
                                        controls
                                        preload="metadata"
                                    />
                                    {/* Badge opcional */}
                                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded pointer-events-none">
                                        <Play className="w-3 h-3 inline mr-1" />
                                        MP4
                                    </div>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex-1 min-w-0">
                                        <span className="text-sm text-gray-700 truncate block">
                                            {mediaFilename}
                                        </span>
                                        {attachmentId && (
                                            <span className="text-xs text-gray-500">
                                                ID: {attachmentId}
                                            </span>
                                        )}
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => setIsMediaLibraryOpen(true)}
                                        disabled={updateMutation.isPending}
                                    >
                                        Trocar
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsMediaLibraryOpen(true)}
                                disabled={updateMutation.isPending}
                                className="mt-1.5 w-full aspect-video bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg hover:border-pink-400 hover:bg-pink-50 transition-colors flex flex-col items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <VideoIcon className="w-12 h-12 text-gray-400" />
                                <span className="text-sm text-gray-600">Clique para selecionar vídeo</span>
                            </button>
                        )}
                    </div>

                    {/* Digitando */}
                    <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                            <MessageCircle className="w-3.5 h-3.5" />
                            Digitando... (segundos)
                        </Label>
                        <Input
                            type="number"
                            min={0}
                            max={30}
                            value={typingSeconds}
                            onChange={(e) => setTypingSeconds(Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
                            placeholder="0"
                            className="h-10 w-32"
                            disabled={updateMutation.isPending}
                        />
                        <p className="text-xs text-gray-500">
                            Mostra "digitando..." por X segundos antes de enviar. 0 = desativado.
                        </p>
                    </div>

                    {/* Info Helper */}
                    <div className="bg-pink-50 border border-pink-200 rounded-lg p-3">
                        <p className="text-xs text-pink-900">
                            💡 <strong>Dica:</strong> Este vídeo será enviado diretamente para o usuário sem botões ou texto adicional.
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                >
                    {updateMutation.isPending ? (
                        'Salvando...'
                    ) : (
                        <>
                            <Save className="w-4 h-4 mr-2" />
                            Salvar Configurações
                        </>
                    )}
                </Button>
            </div>

            {/* Media Library Modal */}
            <MediaLibraryModal
                isOpen={isMediaLibraryOpen}
                onClose={() => setIsMediaLibraryOpen(false)}
                onSelect={handleMediaSelect}
                pageId={pageId}
                mediaType="video"
            />
        </>
    );
}